import clsx from 'clsx';
import type { ReactNode } from 'react';
import { CiMenuBurger } from 'react-icons/ci';
import { RiArrowDownSLine } from 'react-icons/ri';
import { Link, NavLink } from 'react-router-dom';

import logoInk from '@/assets/vh-black-name.svg';
import logoWhite from '@/assets/vh-white-name.svg';
import Logo from '@/assets/white-logo.svg';
import { Button } from '@/components';
import type { NavGroupView } from '@/router/routes.config';
import type { Person } from '@/store/persons/person.dto';
import { useHasHover } from '@hooks/useHasHover.tsx';

import styles from './Drawer.module.scss';

type DesktopSidebarRailProps = {
  onToggle: () => void;
  isOpen: boolean;
  currentLocationHandler: (key: string) => boolean;
  groups: NavGroupView[];
  openGroup: string | null;
  onHoverGroup: (key: string | null) => void;
  onToggleGroup: (key: string) => void;
  currentUser: Person | null;
  isServiceProvider: boolean;
  spLogo: string | null;
  spName: string | null;
  isGuest: boolean;
  /** Expanded = in-flow labeled panel; collapsed = icon-only rail. */
  expanded?: boolean;
  /** Labeled nav rendered when expanded (the same content the mobile drawer uses). */
  menuContent?: ReactNode;
};

export const DesktopSidebarRail = ({
  currentLocationHandler,
  onToggle,
  isOpen,
  groups,
  openGroup,
  onHoverGroup,
  onToggleGroup,
  // currentUser,
  // isServiceProvider,
  // spLogo,
  // spName,
  // isGuest,
  expanded = false,
  menuContent,
}: DesktopSidebarRailProps) => {
  // const { vessel } = useVessels();
  const hasHover = useHasHover();

  if (expanded) {
    return (
      <aside className={styles.desktopSidebarPanel}>
        <div className={styles.drawerCloseRow}>
          <Link className={styles.logo} to="/dashboard" aria-label="VesselHaven">
            <img
              className={styles.logoInk}
              src={logoInk}
              alt=""
              aria-hidden="true"
              draggable={false}
            />
            <img
              className={styles.logoWhite}
              src={logoWhite}
              alt=""
              aria-hidden="true"
              draggable={false}
            />
          </Link>
        </div>
        {/*<div className={styles.desktopSidebarHeader}>*/}
        {/*  {!isGuest && (*/}
        {/*    <NavLink to={isServiceProvider ? '/my-service' : '/my-yachts'}>*/}
        {/*      <div className={styles.drawerProfile}>*/}
        {/*        <div className={styles.drawerAvatarBorder}>*/}
        {/*          <Avatar*/}
        {/*            src={isServiceProvider ? spLogo : (vessel?.image ?? NoYachtFallback)}*/}
        {/*            firstName={isServiceProvider ? (spName ?? 'S') : vessel?.vessel_name}*/}
        {/*            lastName={isServiceProvider ? '' : undefined}*/}
        {/*            size={60}*/}
        {/*            className={styles.drawerAvatar}*/}
        {/*          />*/}
        {/*        </div>*/}
        {/*        <span className={styles.drawerUserName}>*/}
        {/*          {isServiceProvider ? (spName ?? '') : ''}*/}
        {/*        </span>*/}
        {/*      </div>*/}
        {/*    </NavLink>*/}
        {/*  )}*/}
        {/*</div>*/}
        {menuContent}
      </aside>
    );
  }

  return (
    <aside className={styles.desktopRail}>
      <svg
        width="0"
        height="0"
        style={{ position: 'absolute', overflow: 'hidden' }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="rail-icon-gradient"
            gradientUnits="userSpaceOnUse"
            x1="2"
            y1="2"
            x2="22"
            y2="22"
          >
            <stop offset="0%" stopColor="#06141f" />
            <stop offset="65%" stopColor="#102536" />
            <stop offset="100%" stopColor="#1d4a5c" />
          </linearGradient>
        </defs>
      </svg>
      <div className={styles.desktopHeaderLeft}>
        <Button
          type="button"
          className={styles.desktopMenuButton}
          aria-label={isOpen ? 'Close Sidebar' : 'Open Sidebar'}
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <CiMenuBurger />
        </Button>
      </div>
      <div className={styles.desktopRailLogo}>
        <img src={Logo} alt="logo" />
      </div>
      {/*{!isGuest && (*/}
      {/*  <NavLink to={isServiceProvider ? '/my-service' : '/my-yachts'}>*/}
      {/*    <div className={styles.desktopRailAvatarWrap}>*/}
      {/*      <Avatar*/}
      {/*        src={isServiceProvider ? spLogo : (vessel?.image ?? NoYachtFallback)}*/}
      {/*        firstName={isServiceProvider ? (spName ?? 'S') : undefined}*/}
      {/*        lastName={isServiceProvider ? '' : undefined}*/}
      {/*        size={36}*/}
      {/*      />*/}
      {/*    </div>*/}
      {/*  </NavLink>*/}
      {/*)}*/}

      <nav className={styles.desktopRailNav}>
        <div
          className={styles.desktopRailList}
          onMouseLeave={hasHover ? () => onHoverGroup(null) : undefined}
        >
          {groups.map((group) => {
            const [firstItem] = group.items;
            if (group.standalone) {
              return (
                <NavLink
                  key={group.key}
                  className={clsx(
                    currentLocationHandler(firstItem.key)
                      ? styles.desktopRailItemActive
                      : styles.desktopRailItem,
                    group.startsSection && styles.desktopRailSectionStart,
                  )}
                  to={firstItem.to}
                >
                  {group.icon}
                </NavLink>
              );
            }
            const isActive = group.items.some((i) => currentLocationHandler(i.key));
            const groupOpen = openGroup === group.key;
            return (
              <div
                key={group.key}
                className={styles.desktopRailGroup}
                onMouseEnter={hasHover ? () => onHoverGroup(group.key) : undefined}
              >
                <button
                  type="button"
                  className={`${styles.desktopRailGroupBtn} ${
                    isActive ? styles.desktopRailItemActive : styles.desktopRailItem
                  }`}
                  aria-expanded={groupOpen}
                  aria-label={group.label}
                  onClick={() => onToggleGroup(group.key)}
                >
                  <span className={styles.desktopRailGroupIcon}>{group.icon}</span>
                  <span
                    className={`${styles.desktopRailChevron} ${
                      groupOpen ? styles.desktopRailChevronOpen : ''
                    }`}
                  >
                    <RiArrowDownSLine />
                  </span>
                </button>
                <div
                  className={`${styles.desktopRailGroupItemsWrap} ${
                    groupOpen ? styles.desktopRailGroupItemsOpen : ''
                  }`}
                >
                  <div className={styles.desktopRailGroupItems}>
                    {group.items.map((item) => (
                      <NavLink
                        key={item.key}
                        className={clsx(
                          currentLocationHandler(item.key)
                            ? styles.desktopRailItemActive
                            : styles.desktopRailItem,
                          item.locked && styles.navItemLocked,
                        )}
                        to={item.to}
                        tabIndex={groupOpen ? 0 : -1}
                        title={item.locked ? `${item.label} isn’t on your plan.` : item.label}
                      >
                        {item.icon}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </nav>
    </aside>
  );
};
